import { For, Show, createEffect, createMemo, createSignal, onCleanup, onMount } from 'solid-js';
import type { Game, Bet } from '../../game/Game';
import {
  STORAGE_KEY_PROFILE, MIN_BET_AMOUNT, MIN_CASHOUT_MULTIPLIER, MAX_BET_AMOUNT,
  BET_STEP, BET_PRESETS, AUTO_CASHOUT_PRESETS, DEFAULT_USERNAME, DEFAULT_AVATAR,
  DEFAULT_BET_AMOUNTS, DEFAULT_AUTO_CASHOUT, BET_STEP_SMALL, BET_STEP_LARGE,
} from '../../game/constants';
import { gameStore } from '../../store/gameStore';
import { showToast } from './ui/ToastNotification';
import { gameApi, isRealMode } from '../../api';

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const round2 = (v: number) => Math.round(v * 100) / 100;
const snap = (v: number) => round2(clamp(v, MIN_BET_AMOUNT, MAX_BET_AMOUNT));

function slotStore(id: number) {
  const pfx = `mp_slot_${id}_`;
  return {
    get<T>(key: string, fb: T): T { try { const r = localStorage.getItem(pfx + key); if (r != null) return JSON.parse(r); } catch {} return fb; },
    set(key: string, v: unknown) { try { localStorage.setItem(pfx + key, JSON.stringify(v)); } catch {} },
  };
}

function getProfile() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PROFILE);
    if (!raw) return { username: DEFAULT_USERNAME, avatar: DEFAULT_AVATAR } as const;
    const p = JSON.parse(raw);
    return {
      username: (p?.username ? String(p.username).trim() : '') || DEFAULT_USERNAME,
      avatar: p?.avatar ? String(p.avatar) : DEFAULT_AVATAR,
    } as const;
  } catch { return { username: DEFAULT_USERNAME, avatar: DEFAULT_AVATAR } as const; }
}

const resolveGame = (accessor: () => Game | undefined): Game | undefined =>
  accessor() || (window as any).memePumpApp?.getGame?.();

function BetSlot(p: { slotId: 1 | 2; game: () => Game | undefined; multiplier: () => number; phase: () => string | null }) {
  const store = slotStore(p.slotId);

  const [amount, setAmount] = createSignal(store.get('amount', DEFAULT_BET_AMOUNTS[p.slotId === 1 ? 0 : 1]));
  const [autoBet, setAutoBet] = createSignal(false);
  const [acEnabled, setAcEnabled] = createSignal(false);
  const [acValue, setAcValue] = createSignal<number | undefined>(undefined);
  const [bet, setBet] = createSignal<Bet | null>(null);
  const [cashedOut, setCashedOut] = createSignal(false);
  const [placing, setPlacing] = createSignal(false);
  const [pendingAmount, setPendingAmount] = createSignal<number | null>(null);
  const [queued, setQueued] = createSignal(false);
  const [skipOnce, setSkipOnce] = createSignal(false);
  const [prevState, setPrevState] = createSignal<string | null>(null);

  const active = createMemo(() => !!bet()?.isActive);

  const phase = () => {
    const ph = p.phase();
    if (ph) return ph;
    try { return (window as any).memePumpApp?.getGame?.()?.getCurrentState?.() || 'BET_TIME'; } catch { return 'BET_TIME'; }
  };

  const inRound = createMemo(() => phase() === 'RUNNING' || phase() === 'ROUND_START');
  const locked = createMemo(() => active() || placing());
  const disabled = createMemo(() => locked());

  const btnText = createMemo(() => {
    const ph = phase();
    const tgt = acEnabled() && acValue() ? acValue() : undefined;
    if (ph === 'BET_TIME') {
      if (active()) return 'Cancel';
      return tgt ? `Place Bet (Auto @ ${tgt!.toFixed(2)}x)` : 'Place Bet';
    }
    if (ph === 'ROUND_START' || ph === 'RUNNING' || ph === 'CRASH') {
      if (active() && !cashedOut() && ph === 'RUNNING') return `Cash Out @ ${p.multiplier().toFixed(2)}x`;
      return queued() ? 'Cancel Next' : 'Queue Bet';
    }
    return 'Queue Bet';
  });

  const btnMode = createMemo(() => {
    const ph = phase();
    if (ph === 'BET_TIME') return active() ? 'button-danger cancel-mode' : 'button-primary';
    if (ph === 'RUNNING' || ph === 'ROUND_START' || ph === 'CRASH')
      return active() && !cashedOut() && ph === 'RUNNING'
        ? 'button-cta cashout-mode'
        : queued() ? 'button-danger cancel-mode' : 'button-primary';
    return 'button-primary';
  });

  const btnDisabled = createMemo(() => {
    if (placing()) return true;
    if (phase() === 'BET_TIME') return !active() && amount() < MIN_BET_AMOUNT;
    return false;
  });

  createEffect(() => store.set('amount', snap(amount())));

  const placeBet = (): boolean => {
    if (placing()) return false;
    if (amount() > gameStore.balance()) { showToast('error', 'Insufficient balance'); return false; }
    try {
      const requestedAmount = amount();
      const tgt = acEnabled() && acValue() && acValue()! >= MIN_CASHOUT_MULTIPLIER ? acValue()! : undefined;
      gameApi.placeBet({ amount: requestedAmount, slotId: p.slotId, autoCashout: tgt ?? null });
      setPlacing(true);
      setPendingAmount(requestedAmount);

      if (!isRealMode) {
        // In mock mode, Game.placeBet fires synchronously — sync local state
        const game = resolveGame(p.game);
        const found = game ? (game.getBets?.() || []).find((b: Bet) => b.slotId === p.slotId && b.isActive) : undefined;
        if (found) {
          setBet(found);
          setPlacing(false);
          setPendingAmount(null);
        }
      }
      // In real mode, bet:place ack handler will update state
      return true;
    } catch (e) {
      setPlacing(false);
      setPendingAmount(null);
      console.error('Failed to place bet', e);
      return false;
    }
  };

  const cancelBet = () => {
    gameApi.cancelBet({ slotId: p.slotId });
    // In mock mode, Game handles the cancel and fires betCanceled event
  };

  const cashOut = () => {
    const b = bet();
    if (b?.isActive) {
      gameApi.cashout({ slotId: p.slotId });
      // In mock mode, Game handles the cashout and fires cashout event
    }
  };

  const onAction = () => {
    const ph = phase();
    if (ph === 'BET_TIME') { active() ? cancelBet() : void placeBet(); return; }
    if ((ph === 'RUNNING' || ph === 'ROUND_START' || ph === 'CRASH') && !active()) {
      setQueued(v => { const next = !v; if (!next) setSkipOnce(true); return next; });
      return;
    }
    if (ph === 'RUNNING' && active() && !cashedOut()) cashOut();
  };

  const toggleAutoBet = (v: boolean) => {
    setAutoBet(v);
    try { p.game()?.setAutoBet(p.slotId, v); } catch {}
    const ph = phase();
    if (v) {
      if (ph === 'BET_TIME' && !active()) void placeBet();
      else if (!active()) setQueued(true);
    } else if (queued()) setQueued(false);
  };

  const toggleAutoCashout = (on: boolean) => {
    setAcEnabled(on);
    if (on && (typeof acValue() !== 'number' || acValue()! < MIN_CASHOUT_MULTIPLIER))
      setAcValue(DEFAULT_AUTO_CASHOUT);
  };

  const nudge = (d: number) => { if (!disabled()) setAmount(snap((amount() || 0) + d)); };
  const preset = (v: number) => { if (!disabled()) setAmount(snap(v)); };
  const acPreset = (v: number) => { if (acEnabled() && !locked()) setAcValue(round2(clamp(v, MIN_CASHOUT_MULTIPLIER, MAX_BET_AMOUNT))); };

  const slotCls = createMemo(() => {
    const parts = ['bet-slot compact'];
    if (active() && inRound()) parts.push('disabled');
    if (active()) parts.push('active');
    if (p.phase() === 'RUNNING') parts.push('in-round');
    if (cashedOut()) parts.push('cashed');
    if (queued()) parts.push('queued');
    return parts.join(' ');
  });

  const onKeyDown = (e: KeyboardEvent) => {
    if (disabled()) return;
    const { key, shiftKey, ctrlKey, metaKey } = e;
    const mod = ctrlKey || metaKey;
    if (key === 'ArrowUp') { e.preventDefault(); nudge(shiftKey ? BET_STEP_LARGE : BET_STEP_SMALL); }
    else if (key === 'ArrowDown') { e.preventDefault(); nudge(shiftKey ? -BET_STEP_LARGE : -BET_STEP_SMALL); }
    else if (key === 'PageUp') { e.preventDefault(); setAmount(a => snap((a || 0) * 2)); }
    else if (key === 'PageDown') { e.preventDefault(); setAmount(a => snap((a || 0) / 2)); }
    else if (key === 'Enter') { e.preventDefault(); onAction(); }
    else if (mod && (key === '+' || key === '=')) { e.preventDefault(); nudge(BET_STEP_SMALL); }
    else if (mod && key === '-') { e.preventDefault(); nudge(-BET_STEP_SMALL); }
  };

  onMount(() => {
    const onMirror = (ev: Event) => {
      const d = (ev as CustomEvent).detail as { amount?: number; autoCashout?: number; slotId?: 1 | 2; autoPlace?: boolean } | undefined;
      if (!d || typeof d.amount !== 'number') return;
      if (d.slotId && d.slotId !== p.slotId) return;
      setAmount(snap(d.amount));
      if (typeof d.autoCashout === 'number' && d.autoCashout >= MIN_CASHOUT_MULTIPLIER) {
        setAcEnabled(true);
        setAcValue(round2(clamp(d.autoCashout, MIN_CASHOUT_MULTIPLIER, MAX_BET_AMOUNT)));
      } else { setAcEnabled(false); setAcValue(undefined); }
      if (d.autoPlace) {
        if (phase() === 'BET_TIME' && !active()) void placeBet();
        else if (!active()) setQueued(true);
      }
    };
    window.addEventListener('memePump:mirrorBet', onMirror as EventListener);
    onCleanup(() => window.removeEventListener('memePump:mirrorBet', onMirror as EventListener));

    // Subscribe to API errors for this slot
    const unsubError = gameApi.on('error', () => {
      setPlacing(false);
      setPendingAmount(null);
    });
    onCleanup(unsubError);

    // ── Real mode: track bets from server events ─────
    const attachToApi = () => {
      const unsubs: (() => void)[] = [];

      unsubs.push(gameApi.on('bet:place', (payload) => {
        if (payload.slotId === p.slotId) {
          const acceptedAmount = pendingAmount() ?? amount();
          setBet({ id: `ws_${p.slotId}_${Date.now()}`, slotId: p.slotId, amount: acceptedAmount, isActive: true } as Bet);
          setCashedOut(false);
          setPlacing(false);
          setPendingAmount(null);
        }
      }));

      unsubs.push(gameApi.on('bet:cancel', (payload) => {
        if (payload.slotId === p.slotId) {
          setBet(null);
          setCashedOut(false);
          setPlacing(false);
          setPendingAmount(null);
        }
      }));

      unsubs.push(gameApi.on('bet:cashout', (payload) => {
        if (payload.slotId === p.slotId && bet()) {
          setBet({ ...bet()!, isActive: false } as Bet);
          setCashedOut(true);
          if (autoBet()) { setQueued(true); setSkipOnce(false); }
        }
      }));

      unsubs.push(gameApi.on('auto:cashout', (payload) => {
        if (payload.slotId === p.slotId && bet()) {
          setBet({ ...bet()!, isActive: false, cashoutMultiplier: payload.multiplier } as Bet);
          setCashedOut(true);
          if (autoBet()) { setQueued(true); setSkipOnce(false); }
        }
      }));

      unsubs.push(gameApi.on('round:state', (payload) => {
        const myId = gameStore.playerId();
        if (payload.state === 'BET_TIME') {
          if (bet() && !bet()!.isActive) { setBet(null); setCashedOut(false); }
          if (!bet() && !placing() && queued()) {
            queueMicrotask(() => { if (placeBet()) { setQueued(false); setSkipOnce(false); } });
          }
          if (autoBet() && !bet() && !queued() && !placing()) {
            queueMicrotask(() => { if (placeBet()) { setSkipOnce(false); } });
          }
        } else if (payload.state === 'RUNNING' && myId != null) {
          const myBet = payload.bets.find(b => b.playerId === myId && b.slotId === p.slotId);
          if (myBet) {
            setBet({ id: myBet.betId, slotId: myBet.slotId, amount: myBet.amount, isActive: true, cashoutMultiplier: myBet.cashoutMultiplier } as Bet);
            setCashedOut(false);
          }
        } else if (payload.state === 'CRASH') {
          if (bet()?.isActive && !cashedOut()) {
            setBet({ ...bet()!, isActive: false } as Bet);
          }
          if (autoBet() && !queued()) { setQueued(true); setSkipOnce(false); }
        }
        setPrevState(payload.state);
      }));

      onCleanup(() => unsubs.forEach(u => u()));
    };

    // ── Mock mode: track bets from Game engine ───────
    const attach = () => {
      const game = resolveGame(p.game); if (!game) return false;
      try { game.setAutoBet(p.slotId, autoBet()); } catch {}

      const syncBet = (b: Bet) => { if (!bet() || bet()!.id !== b.id) { setBet(b); setCashedOut(false); } };
      const deactivate = (b: Bet) => { if (bet() && b.id === bet()!.id) setBet({ ...bet()!, isActive: false } as Bet); };

      game.on('stateChange', (e: any) => {
        const st = e?.data?.state as string;
        if (st === 'RUNNING' || st === 'ROUND_START') {
          try {
            const found = (game.getBets?.() || []).find((b: any) => b.slotId === p.slotId && b.isActive);
            if (found) syncBet(found);
          } catch {}
          if (autoBet() && !active() && !queued()) {
            if (skipOnce()) { if (prevState() === 'BET_TIME') { setSkipOnce(false); setQueued(true); } }
            else setQueued(true);
          }
        }
        if (st === 'BET_TIME') {
          if (!active() && !placing() && queued()) { if (placeBet()) { setQueued(false); setSkipOnce(false); } }
          if (bet() && !bet()!.isActive) { setBet(null); setCashedOut(false); }
        }
        setPrevState(st);
      });

      game.on('cashout', (e: any) => {
        const b: Bet | undefined = e?.data?.bet;
        if (b && bet() && b.id === bet()!.id) {
          setBet({ ...bet()!, isActive: false, cashoutMultiplier: e?.data?.multiplier } as Bet);
          setCashedOut(true);
          if (autoBet()) { setQueued(true); setSkipOnce(false); }
        }
      });

      game.on('betPlaced', (e: any) => { const b: Bet | undefined = e?.data?.bet; if (b?.slotId === p.slotId) syncBet(b); });
      game.on('betActivated', (e: any) => {
        const b: Bet | undefined = e?.data?.bet;
        if (b?.slotId === p.slotId && (!bet() || bet()!.id !== b.id || !bet()!.isActive)) { setBet(b); setCashedOut(false); }
      });
      game.on('betCanceled', (e: any) => { const b: Bet | undefined = e?.data?.bet; if (b && bet()?.id === b.id) { setBet(null); setCashedOut(false); } });
      game.on('betWin', (e: any) => { const b: Bet | undefined = e?.data?.bet; if (b) deactivate(b); });
      game.on('betLoss', (e: any) => { const b: Bet | undefined = e?.data?.bet; if (b) deactivate(b); });

      onCleanup(() => {});
      return true;
    };

    if (isRealMode) {
      attachToApi();
    } else if (!attach()) {
      const onReady = () => { attach(); };
      window.addEventListener('memePump:ready', onReady, { once: true });
      onCleanup(() => window.removeEventListener('memePump:ready', onReady));
    }
  });

  const parseInput = (el: HTMLInputElement) => {
    const v = parseFloat(el.value.replace(',', '.'));
    return isNaN(v) ? undefined : v;
  };

  const clampDecimalInput = (el: HTMLInputElement) => {
    const raw = el.value;
    const dot = raw.indexOf('.');
    if (dot !== -1 && raw.length - dot - 1 > 2) el.value = raw.slice(0, dot + 3);
  };

  return (
    <div class={slotCls()} id={`bet-slot-${p.slotId}`}>
      <div class="slot-top">
        <div class="auto-cashout-row" data-enabled={acEnabled() ? 'true' : 'false'}>
          <label class="mini-toggle auto-cashout-toggle">
            <input type="checkbox" checked={acEnabled()} onInput={(e) => toggleAutoCashout((e.currentTarget as HTMLInputElement).checked)} disabled={locked()} />
            <span class="mini-switch" aria-hidden="true" />
            <span class="toggle-text">Auto Cashout</span>
          </label>
          <div class="auto-cashout-inline" data-enabled={acEnabled() ? 'true' : 'false'}>
            <input
              type="number"
              id={`auto-cashout-${p.slotId}`}
              class="auto-cashout-input"
              min={String(MIN_CASHOUT_MULTIPLIER)}
              step={String(BET_STEP)}
              placeholder={DEFAULT_AUTO_CASHOUT.toFixed(2)}
              value={acValue() === undefined ? '' : acValue()!.toFixed(2)}
              disabled={!acEnabled() || locked()}
              onInput={(e) => { const v = parseInput(e.currentTarget as HTMLInputElement); if (v !== undefined) setAcValue(round2(clamp(v, MIN_CASHOUT_MULTIPLIER, MAX_BET_AMOUNT))); }}
              aria-invalid={acEnabled() && (acValue() ? acValue()! < MIN_CASHOUT_MULTIPLIER : true)}
            />
            <span class="suffix">x</span>
          </div>
          <Show when={acEnabled()}>
            <div class="auto-cashout-presets" data-for={String(p.slotId)}>
              <For each={[...AUTO_CASHOUT_PRESETS] as number[]}>{(v) => (
                <button
                  type="button"
                  class={acValue() === v ? 'ac-preset selected' : 'ac-preset'}
                  disabled={!acEnabled() || locked()}
                  onClick={() => acPreset(v)}
                  data-mult={String(v)}
                >{v.toFixed(v < 2 && v % 1 !== 0 ? 1 : 0)}x</button>
              )}</For>
            </div>
          </Show>
        </div>

        <div class="auto-bet-row">
          <label class="mini-toggle auto-bet-toggle">
            <input type="checkbox" checked={autoBet()} onInput={(e) => toggleAutoBet((e.currentTarget as HTMLInputElement).checked)} />
            <span class="mini-switch" aria-hidden="true" />
            <span class="toggle-text">Auto Bet</span>
          </label>
          <div class="preset-buttons bet-amount-presets" data-for={String(p.slotId)}>
            <For each={[...BET_PRESETS] as number[]}>
              {(v) => <button class="preset-btn" type="button" onClick={() => preset(v)} disabled={disabled()} data-value={String(v)}>{v}</button>}
            </For>
          </div>
        </div>
      </div>

      <div class="slot-bottom">
        <div class="bet-input-group">
          <label for={`bet-amount-${p.slotId}`} class="bet-label">Bet Amount</label>
          <div class="bet-amount-stepper" data-slot={String(p.slotId)}>
            <button class="amount-step minus" type="button" aria-label="Decrease by 10 cents" disabled={disabled()} onClick={() => nudge(-BET_STEP_SMALL)} data-step="-0.10">−</button>
            <input
              id={`bet-amount-${p.slotId}`}
              data-autofocus={p.slotId === 1}
              type="number"
              class="bet-amount"
              min={String(MIN_BET_AMOUNT)}
              step={String(BET_STEP)}
              placeholder="0.00"
              value={amount().toFixed(2)}
              disabled={disabled()}
              onInput={(e) => { const el = e.currentTarget as HTMLInputElement; clampDecimalInput(el); const v = parseInput(el); if (v !== undefined) setAmount(round2(Math.max(MIN_BET_AMOUNT, v))); }}
              onBlur={() => setAmount(v => round2(Math.max(MIN_BET_AMOUNT, v)))}
              onKeyDown={onKeyDown as any}
              aria-invalid={amount() < MIN_BET_AMOUNT}
              aria-describedby={`amt-err-${p.slotId}`}
            />
            <button class="amount-step plus" type="button" aria-label="Increase by 10 cents" disabled={disabled()} onClick={() => nudge(BET_STEP_SMALL)} data-step="0.10">+</button>
          </div>
          <Show when={amount() < MIN_BET_AMOUNT}>
            <div id={`amt-err-${p.slotId}`} class="field-hint error" role="status" aria-live="polite">Min ${MIN_BET_AMOUNT.toFixed(2)}</div>
          </Show>
        </div>
        <div class="bet-action-wrap">
          <div class="queue-pill-slot" data-visible={queued() && (p.phase() === 'RUNNING' || p.phase() === 'ROUND_START' || p.phase() === 'CRASH') ? 'true' : 'false'}>
            <div class="queue-pill" title="Queued for next round">Queued</div>
          </div>
          <button class={`place-bet-btn ${btnMode()}`} data-slot={String(p.slotId)} disabled={btnDisabled()} onClick={onAction}>{btnText()}</button>
        </div>
      </div>
    </div>
  );
}

export default function BetPanelSolid() {
  const { engine, phase, multiplier } = gameStore;
  createEffect(() => { void phase(); void multiplier(); });
  return (<>
    <BetSlot slotId={1} game={() => engine()} phase={() => phase() as any} multiplier={() => multiplier()} />
    <BetSlot slotId={2} game={() => engine()} phase={() => phase() as any} multiplier={() => multiplier()} />
  </>);
}
